import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { Modal, CurrencyAvatar } from '@/components'
import { ChevronRight } from '@/assets/icons'
import { useTheme } from '@/hooks/useTheme'
import type { WalletCurrency } from '@/domain'

interface SellMoneyCurrencyBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  currencies: WalletCurrency[]
  selectedCurrency?: WalletCurrency
  onSelectCurrency: (currency: WalletCurrency) => void
}

export const SellMoneyCurrencyBottomSheet: React.FC<
  SellMoneyCurrencyBottomSheetProps
> = ({ isOpen, onClose, currencies, onSelectCurrency }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const handleCurrencySelect = (currency: WalletCurrency) => {
    onSelectCurrency(currency)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('screens:addMoney.selectCurrency', 'Select Currency to Sell')}
      showHandle={true}
    >
      <BaseView
        style={{
          gap: 12,
          paddingBottom: 20,
          scrollbarWidth: 'none',
        }}
      >
        {currencies.map((currency) => (
          <BaseView
            key={currency.code}
            row
            align="center"
            gap={12}
            p={12}
            rounded={24}
            style={{
              backgroundColor: theme.colors.background.cardItemRoot,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            className="hover:opacity-80"
            onClick={() => handleCurrencySelect(currency)}
          >
            {/* Currency Icon */}
            <CurrencyAvatar
              uri={currency.icon}
              bgColor={currency.avatarBGRefColor}
              size={48}
              iconSize={26}
              fallbackText={currency.code?.substring(0, 2) || '?'}
            />

            {/* Currency Info */}
            <BaseView flex={1} gap={2}>
              <BaseText
                fontSize={16}
                fontFamily="semibold"
                color="label.default"
              >
                {currency.name}
              </BaseText>
              <BaseText
                fontSize={14}
                fontFamily="regular"
                color="label.description"
              >
                {currency.code}
              </BaseText>
            </BaseView>

            {/* Chevron Right */}
            <ChevronRight size={38} color={theme.colors.label.description} />
          </BaseView>
        ))}
      </BaseView>
    </Modal>
  )
}
